import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { consigneesAPI, inventoryItemsAPI, hsCodesAPI } from '../../services/api';
import Table from '../Common/Table';
import Modal from '../Common/Modal';

const InventoryItemsManagement = () => {
  const [items, setItems] = useState([]);
  const [consignees, setConsignees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterConsigneeId, setFilterConsigneeId] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFromItem, setTransferFromItem] = useState(null);
  const [transferData, setTransferData] = useState({
    to_consignee_id: '',
    quantity: '',
  });
  const [formData, setFormData] = useState({
    consignee_id: '',
    hs_code_id: '',
    description: '',
    quantity: '',
    low_stock_threshold: '',
    value: '',
    weight: '',
    length_feet: '',
    width_feet: '',
    height_feet: '',
  });
  const [htsQuery, setHtsQuery] = useState('');
  const [htsResults, setHtsResults] = useState([]);
  const [htsSummary, setHtsSummary] = useState('');
  const [htsSearchLoading, setHtsSearchLoading] = useState(false);
  const [htsSearchError, setHtsSearchError] = useState('');
  const [htsMenuBox, setHtsMenuBox] = useState(null);
  const htsAnchorRef = useRef(null);
  const csvImportInputRef = useRef(null);
  const [lowStockCount, setLowStockCount] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (htsQuery.trim().length < 2) {
      setHtsResults([]);
      setHtsSearchError('');
      setHtsSearchLoading(false);
      return;
    }
    setHtsSearchLoading(true);
    setHtsSearchError('');
    const q = htsQuery.trim();
    const t = setTimeout(async () => {
      try {
        const rows = await hsCodesAPI.search(q, 40);
        setHtsResults(Array.isArray(rows) ? rows : []);
      } catch (err) {
        setHtsSearchError(err.message || 'HTS search failed');
        setHtsResults([]);
      } finally {
        setHtsSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [htsQuery]);

  useLayoutEffect(() => {
    if (!htsAnchorRef.current || htsResults.length === 0) {
      setHtsMenuBox(null);
      return;
    }
    const r = htsAnchorRef.current.getBoundingClientRect();
    const maxH = Math.min(220, Math.max(80, window.innerHeight - r.bottom - 16));
    setHtsMenuBox({
      left: r.left,
      top: r.bottom + 4,
      width: Math.min(r.width, window.innerWidth - r.left - 8),
      maxHeight: maxH,
    });
  }, [htsResults]);

  useEffect(() => {
    if (htsResults.length === 0) return undefined;
    const reposition = () => {
      if (!htsAnchorRef.current) return;
      const r = htsAnchorRef.current.getBoundingClientRect();
      const maxH = Math.min(220, Math.max(80, window.innerHeight - r.bottom - 16));
      setHtsMenuBox({
        left: r.left,
        top: r.bottom + 4,
        width: Math.min(r.width, window.innerWidth - r.left - 8),
        maxHeight: maxH,
      });
    };
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [htsResults]);

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line no-use-before-define
  }, [filterConsigneeId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await inventoryItemsAPI.getLowStock(
          filterConsigneeId ? { consignee_id: filterConsigneeId } : {}
        );
        if (!cancelled) setLowStockCount((d.items || []).length);
      } catch {
        if (!cancelled) setLowStockCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [filterConsigneeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [itemsData, consigneesData] = await Promise.all([
        inventoryItemsAPI.getAll(),
        consigneesAPI.getAll(),
      ]);
      setItems(itemsData);
      setConsignees(consigneesData);
    } catch (error) {
      alert('Error loading inventory items: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await inventoryItemsAPI.getAll(
        filterConsigneeId ? { consignee_id: filterConsigneeId } : {}
      );
      setItems(data);
    } catch (error) {
      alert('Error loading inventory items: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      consignee_id: '',
      hs_code_id: '',
      description: '',
      quantity: '',
      low_stock_threshold: '',
      value: '',
      weight: '',
      length_feet: '',
      width_feet: '',
      height_feet: '',
    });
    setHtsQuery('');
    setHtsResults([]);
    setHtsSummary('');
    setHtsSearchError('');
    setHtsSearchLoading(false);
    setHtsMenuBox(null);
  };

  const resetTransfer = () => {
    setTransferFromItem(null);
    setTransferData({ to_consignee_id: '', quantity: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const hsRaw = formData.hs_code_id;
      let hsCodeId = null;
      if (hsRaw !== '' && hsRaw != null) {
        const n = parseInt(hsRaw, 10);
        hsCodeId = Number.isFinite(n) && n > 0 ? n : null;
      }
      const submitData = {
        consignee_id: parseInt(formData.consignee_id),
        hs_code_id: hsCodeId,
        description: formData.description || null,
        quantity: formData.quantity === '' ? null : parseInt(formData.quantity),
        value: formData.value === '' ? null : parseFloat(formData.value),
        weight: formData.weight === '' ? null : parseFloat(formData.weight),
        length_feet: formData.length_feet === '' ? null : parseFloat(formData.length_feet),
        width_feet: formData.width_feet === '' ? null : parseFloat(formData.width_feet),
        height_feet: formData.height_feet === '' ? null : parseFloat(formData.height_feet),
      };
      if (formData.low_stock_threshold === '' || formData.low_stock_threshold === undefined) {
        submitData.low_stock_threshold = null;
      } else {
        const lst = parseInt(formData.low_stock_threshold, 10);
        if (!Number.isFinite(lst) || lst < -1) {
          alert('Low stock threshold must be an integer ≥ -1 (blank = server default, -1 = off).');
          return;
        }
        submitData.low_stock_threshold = lst;
      }

      if (editingItem) {
        await inventoryItemsAPI.update(editingItem.inventory_items_id, submitData);
      } else {
        await inventoryItemsAPI.create(submitData);
      }
      setShowModal(false);
      setEditingItem(null);
      resetForm();
      loadItems();
    } catch (error) {
      alert('Error saving inventory item: ' + error.message);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      consignee_id: item.consignee_id,
      hs_code_id: item.hs_code_id != null ? String(item.hs_code_id) : '',
      description: item.description || '',
      quantity: item.quantity ?? '',
      low_stock_threshold:
        item.low_stock_threshold === -1
          ? '-1'
          : item.low_stock_threshold != null
            ? String(item.low_stock_threshold)
            : '',
      value: item.value ?? '',
      weight: item.weight ?? '',
      length_feet: item.length_feet ?? '',
      width_feet: item.width_feet ?? '',
      height_feet: item.height_feet ?? '',
    });
    setHtsSummary(
      item.hs_hts_number
        ? `${item.hs_hts_number}${item.hs_description_preview ? ` — ${item.hs_description_preview}` : ''}`
        : ''
    );
    setHtsQuery('');
    setHtsResults([]);
    setHtsSearchError('');
    setHtsSearchLoading(false);
    setHtsMenuBox(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inventory item?')) return;
    try {
      await inventoryItemsAPI.delete(id);
      loadItems();
    } catch (error) {
      alert('Error deleting inventory item: ' + error.message);
    }
  };

  const columns = [
    { key: 'inventory_items_id', label: 'ID' },
    { key: 'consignee_name', label: 'Consignee' },
    {
      key: 'hs_hts_number',
      label: 'HTS',
      format: (val) => val || '—',
    },
    { key: 'description', label: 'Description' },
    {
      key: 'quantity',
      label: 'Qty',
      format: (val, row) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
          <span>{val != null && val !== '' ? val : '—'}</span>
          {Number(row?.is_low_stock) === 1 ? (
            <span title="At or below low-stock threshold" aria-label="Low stock">
              ⚠️
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'low_stock_threshold',
      label: 'Low stock ≤',
      format: (v) => (v === -1 ? 'Off' : v == null ? 'Default' : v),
    },
    { key: 'value', label: 'Value', format: (val) => val != null ? `$${parseFloat(val).toFixed(2)}` : '-' },
    { key: 'weight', label: 'Weight', format: (val) => val != null ? parseFloat(val).toFixed(2) : '-' },
    { key: 'length_feet', label: 'L (ft)' },
    { key: 'width_feet', label: 'W (ft)' },
    { key: 'height_feet', label: 'H (ft)' },
  ];

  const handleTransfer = (item) => {
    if (item.quantity == null || parseInt(item.quantity) <= 0) {
      alert('This item has no quantity available to transfer.');
      return;
    }
    setTransferFromItem(item);
    setTransferData({
      to_consignee_id: '',
      quantity: '',
    });
    setShowTransferModal(true);
  };

  const submitTransfer = async (e) => {
    e.preventDefault();
    if (!transferFromItem) return;
    try {
      const qty = parseInt(transferData.quantity);
      const toId = parseInt(transferData.to_consignee_id);
      if (!toId || !qty || qty <= 0) {
        alert('Please select a destination consignee and enter a quantity > 0.');
        return;
      }
      if (toId === transferFromItem.consignee_id) {
        alert('Destination consignee must be different from source.');
        return;
      }
      if (qty > parseInt(transferFromItem.quantity)) {
        alert('Transfer quantity exceeds available quantity.');
        return;
      }
      await inventoryItemsAPI.transfer(transferFromItem.inventory_items_id, {
        to_consignee_id: toId,
        quantity: qty,
      });
      setShowTransferModal(false);
      resetTransfer();
      loadItems();
    } catch (error) {
      alert('Error transferring inventory: ' + error.message);
    }
  };

  let isAdmin = false;
  try {
    isAdmin = JSON.parse(localStorage.getItem('user') || 'null')?.role === 'admin';
  } catch {
    isAdmin = false;
  }

  const handleExportCsv = async () => {
    try {
      const blob = await inventoryItemsAPI.exportCsv(filterConsigneeId || '');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory_items_export.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  };

  const handleImportCsvFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const r = await inventoryItemsAPI.importCsv(file);
      const errs = (r.errors || []).slice(0, 8).map((x) => `Line ${x.line}: ${x.message}`).join('\n');
      const more = (r.errors || []).length > 8 ? '\n…' : '';
      alert(
        `Import finished.\nInserted: ${r.inserted}\nUpdated: ${r.updated}\nSkipped empty rows: ${r.skipped}\nRow errors: ${(r.errors || []).length}${errs ? `\n\n${errs}${more}` : ''}`
      );
      loadItems();
    } catch (err) {
      alert('Import failed: ' + err.message);
    }
  };

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Inventory Items</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {isAdmin && (
            <>
              <input
                ref={csvImportInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={handleImportCsvFile}
              />
              <button type="button" className="btn-secondary" onClick={handleExportCsv} title="Uses the consignee filter when set; UTF-8 CSV for Excel">
                Export CSV
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => csvImportInputRef.current?.click()}
                title="Rows without inventory_items_id insert; with id update. Needs consignee_id or consignee_name."
              >
                Import CSV
              </button>
            </>
          )}
          <button className="btn-primary" onClick={() => { setEditingItem(null); resetForm(); setShowModal(true); }}>
            Add Inventory Item
          </button>
        </div>
      </div>

      {lowStockCount != null && lowStockCount > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px 16px',
            background: '#fff8e6',
            border: '1px solid #f0c020',
            borderRadius: 8,
            color: '#5c4a00',
          }}
        >
          <strong>{lowStockCount}</strong> line{lowStockCount === 1 ? '' : 's'} at or below the low-stock threshold for this view.{' '}
          <Link to="/inventory-low-stock">Open low stock alerts</Link>
        </div>
      )}

      <div className="form-row" style={{ marginBottom: 12 }}>
        <div className="form-group" style={{ maxWidth: 360 }}>
          <label>Filter by Consignee</label>
          <select value={filterConsigneeId} onChange={(e) => setFilterConsigneeId(e.target.value)}>
            <option value="">All Consignees</option>
            {consignees.map((c) => (
              <option key={c.id} value={c.id}>{c.company_name}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <Table
          rowKey="inventory_items_id"
          data={items}
          columns={columns}
          onEdit={handleEdit}
          onDelete={handleDelete}
          renderActions={(row) => (
            <button
              className="btn-secondary"
              style={{ marginRight: 8 }}
              onClick={() => handleTransfer(row)}
              title="Transfer quantity to another consignee"
            >
              🔁
            </button>
          )}
        />
      )}

      <Modal
        show={showModal}
        onClose={() => { setShowModal(false); setEditingItem(null); resetForm(); }}
        title={editingItem ? 'Edit Inventory Item' : 'Add Inventory Item'}
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Consignee *</label>
            <select
              value={formData.consignee_id}
              onChange={(e) => setFormData({ ...formData, consignee_id: e.target.value })}
              required
            >
              <option value="">Select Consignee</option>
              {consignees.map((c) => (
                <option key={c.id} value={c.id}>{c.company_name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>HTS / tariff lookup (US)</label>
            <div ref={htsAnchorRef}>
              <input
                type="text"
                placeholder="Type at least 2 characters (code or description)…"
                value={htsQuery}
                onChange={(e) => setHtsQuery(e.target.value)}
                autoComplete="off"
              />
            </div>
            {htsSearchLoading && (
              <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#666' }}>Searching…</div>
            )}
            {htsSearchError && (
              <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#c0392b' }}>{htsSearchError}</div>
            )}
            {!htsSearchLoading && !htsSearchError && htsQuery.trim().length >= 2 && htsResults.length === 0 && (
              <div style={{ marginTop: 8, fontSize: '0.85rem', color: '#666' }}>
                No matches. If <code>hs_codes</code> is empty, use <strong>Admin → HS / HTS codes</strong> to import the US HTS CSV.
              </div>
            )}
            {htsResults.length > 0 && htsMenuBox && createPortal(
              <div
                role="listbox"
                style={{
                  position: 'fixed',
                  left: htsMenuBox.left,
                  top: htsMenuBox.top,
                  width: htsMenuBox.width,
                  maxHeight: htsMenuBox.maxHeight,
                  overflowY: 'auto',
                  border: '1px solid #ccc',
                  borderRadius: 6,
                  background: '#fff',
                  zIndex: 10001,
                  boxShadow: '0 6px 24px rgba(0,0,0,0.18)',
                }}
              >
                {htsResults.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => {
                      setFormData({ ...formData, hs_code_id: String(r.id) });
                      setHtsSummary(`${r.hts_number_raw} — ${(r.description || '').slice(0, 140)}`);
                      setHtsQuery('');
                      setHtsResults([]);
                      setHtsMenuBox(null);
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '8px 10px',
                      border: 'none',
                      borderBottom: '1px solid #eee',
                      background: 'transparent',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                    }}
                  >
                    <strong>{r.hts_number_raw}</strong>{' '}
                    <span style={{ color: '#555' }}>{(r.description || '').slice(0, 160)}</span>
                  </button>
                ))}
              </div>,
              document.body
            )}
            {htsSummary && (
              <div style={{ marginTop: 10, fontSize: '0.9rem' }}>
                <span style={{ color: '#333' }}>Selected: {htsSummary}</span>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ marginLeft: 10, padding: '4px 10px', fontSize: '0.85rem' }}
                  onClick={() => {
                    setFormData({ ...formData, hs_code_id: '' });
                    setHtsSummary('');
                  }}
                >
                  Clear HTS
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Quantity</label>
              <input
                type="number"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Value ($)</label>
              <input
                type="number"
                step="0.01"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Weight</label>
              <input
                type="number"
                step="0.01"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Low stock threshold (optional)</label>
            <input
              type="number"
              min="-1"
              step="1"
              placeholder="Blank = server default; -1 = no alert"
              value={formData.low_stock_threshold}
              onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
            />
            <div style={{ marginTop: 6, fontSize: '0.8rem', color: '#666' }}>
              Alert when quantity is at or below this value. Leave blank to use the default from the server (often 10). Use <strong>-1</strong> to disable alerts for this line.
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Length (feet)</label>
              <input
                type="number"
                step="0.01"
                value={formData.length_feet}
                onChange={(e) => setFormData({ ...formData, length_feet: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Width (feet)</label>
              <input
                type="number"
                step="0.01"
                value={formData.width_feet}
                onChange={(e) => setFormData({ ...formData, width_feet: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Height (feet)</label>
              <input
                type="number"
                step="0.01"
                value={formData.height_feet}
                onChange={(e) => setFormData({ ...formData, height_feet: e.target.value })}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingItem(null); resetForm(); }}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">Save</button>
          </div>
        </form>
      </Modal>

      <Modal
        show={showTransferModal}
        onClose={() => { setShowTransferModal(false); resetTransfer(); }}
        title="Transfer Inventory"
      >
        {transferFromItem && (
          <form onSubmit={submitTransfer}>
            <div className="form-group">
              <strong>From:</strong> {transferFromItem.consignee_name} (Item #{transferFromItem.inventory_items_id})
            </div>
            <div className="form-group">
              <strong>Available Qty:</strong> {transferFromItem.quantity}
            </div>

            <div className="form-group">
              <label>To Consignee *</label>
              <select
                value={transferData.to_consignee_id}
                onChange={(e) => setTransferData({ ...transferData, to_consignee_id: e.target.value })}
                required
              >
                <option value="">Select Consignee</option>
                {consignees
                  .filter((c) => c.id !== transferFromItem.consignee_id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>{c.company_name}</option>
                  ))}
              </select>
            </div>

            <div className="form-group">
              <label>Quantity to Transfer *</label>
              <input
                type="number"
                min="1"
                value={transferData.quantity}
                onChange={(e) => setTransferData({ ...transferData, quantity: e.target.value })}
                required
              />
            </div>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={() => { setShowTransferModal(false); resetTransfer(); }}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">Transfer</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default InventoryItemsManagement;

